import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MonthlyReport, MonthlyReportIndexStatus } from '../../../../models/monthly-report.model';
import { MonthlyReportService } from '../../../../services/monthly-report.service';
import { DecimalInputComponent } from '../../../common/decimal-input.component';
import { FormsModule } from '@angular/forms';
import { blankOpen, getPool } from '../../../../misc/functions';
import { PoolTypeMap } from '../../../../models/weekly-report.model';
import { bccomp, bcsub } from '../../../../misc/bcmath';
import { AdminUsersService } from '../../../../services/admin-users.service';

interface DialogData {
  report: MonthlyReport,
  status: typeof MonthlyReportIndexStatus[number];
};

@Component({
  standalone: true,
  selector: 'app-report-edit-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    DecimalInputComponent,
    FormsModule
  ],
  templateUrl: './admin-monthly-report-edit.dialog.component.html',
})
export class AdminMonthlyReportEditDialogComponent {
  saving = false;
  report: any;
  status: typeof MonthlyReportIndexStatus[number];

  getPool = getPool;
  PoolTypeMap = PoolTypeMap;

  isRebuildMode: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData,
    private dialogRef: MatDialogRef<AdminMonthlyReportEditDialogComponent, MonthlyReport | undefined>,
    private fb: FormBuilder,
    private monthlyService: MonthlyReportService,
    public adminUsersService: AdminUsersService
  ) {
    this.report = structuredClone(this.dialogData.report);
    this.report.fullWithdrawal = false;
    this.status = this.dialogData.status;

    // 全額出金・全額振替フラグが経ってたら残高とEOM_HWMを０にする
    if (this.report.is_full_withdrawal || this.report.is_full_transfer) {
      this.report.bal_b = '0';
      this.report.eom_high_water_mark = '0';
    }
  }

  calcChg() {
    const [y, m] = (this.report.enroll as any).split('-').map(Number);
    if (!y || !m) {
      this.report.chg = undefined;
    }

    const months = Math.max(0, (this.report.res_b | 0) - 1);
    const end = new Date(y, (m - 1) + months + 1, 0); // 月末

    const yyyy = end.getFullYear();
    const mm = String(end.getMonth() + 1).padStart(2, '0');
    const dd = String(end.getDate()).padStart(2, '0');

    (this.report.chg as any) = `${yyyy}-${mm}-${dd}`;
  }

  onCancel(): void {
    this.dialogRef.close(); // 何も返さない = 親側では何もしない
  }

  get isValid() {
    return ['hdc', 'enroll', 'res_b', 'chg'].every(x => (this.report as any)[x] && (this.report as any)[x].toString() !== '') &&
      ['pension', 'shukkin', 'transfer_amount'].every(x => !(this.report as any)[x] || (this.report as any)[x] > 0) &&
      this.isAdjustValid &&
      this.isHcbValid &&
      this.isTransferPoolValid &&
      this.isFlgValid &&
      this.isCmmValid
  }

  get estimate() {
    let result = bcsub(this.report.balance_before_fees, this.report.hc_a ?? 0);
    result = bcsub(result, this.report.management_fee ?? 0);
    result = bcsub(result, this.report.hc_b ?? 0);
    result = bcsub(result, this.report.pension ?? 0);
    result = bcsub(result, this.report.shukkin ?? 0);
    result = bcsub(result, this.report.transfer_amount ?? 0);
    return result;
  }

  getRemaining() {
    let result = bcsub(this.estimate, 0);
    return result;
  }

  get isNegativeEstimate() {
    return bccomp(this.estimate, 0) < 0;
  }

  onSave(): void {
    this.saving = true;

    // ★ API で更新
    this.monthlyService.update(this.report, this.status, this.isRebuildMode).subscribe({
      next: (updated: MonthlyReport) => {
        this.saving = false;
        // ★ 更新後の行データを親へ返す
        this.dialogRef.close(updated);

        // 再構築モードなら当人の履歴画面を表示
        if (this.isRebuildMode) {
          this.adminUsersService.searchUsers(updated.login_user_id, '', '').subscribe(res => {
            const users = this.adminUsersService.parseData(res);
            blankOpen(undefined, `/client/monthlyReport?uid=${users[0].id}`);
          });
        }
      },
      error: (err: any) => {
        console.error(err);
        this.saving = false;
        // TODO: Snackbar などでエラー表示（既に共通化済ならそこで）
      },
    });
  }

  get isHdcValid() {
    return !!this.report.hdc;
  }

  get isAdjustValid() {
    // adjust_amount が入力されてて、adjust_comment が空だと false
    if (!!this.report.adjust_amount && !this.report.adjust_comment) return false;
    return true;
  }

  get isHcbValid() {
    // h_cb が入力されてて、hc_b_comment が空だと false
    if (!!this.report.hc_b && !this.report.hc_b_comment) return false;
    return true;
  }


  get isTransferPoolValid() {
    // transfer_pool が選択されてて、transfer_amount か transfer_hdc が空だと false
    if (!!this.report.transfer_pool && (!this.report.transfer_amount || !this.report.transfer_hdc)) return false;
    // transfer_amount が入力されてて、transfer_pool か transfer_hdc が空だと false
    if (this.report.transfer_amount > 0 && (!this.report.transfer_pool || !this.report.transfer_hdc)) return false;
    // transfer_hdc が入力されてて、transfer_pool か transfer_amount が空だと false
    if (!!this.report.transfer_hdc && (!this.report.transfer_pool || !this.report.transfer_amount)) return false;

    return true;
  }

  get isFlgValid() {
    return !this.report.flg || this.report.flg === 'JAC';
  }

  get isCmmValid() {
    // JAC なのにカラだと false
    if (this.report.flg === 'JAC' && !this.report.cmm) return false;
    return true;
  }

  get isAmountLocked() {
    return this.report.is_full_withdrawal || this.report.is_full_transfer;
  }

  onFullWithdrawalChange(event: MatCheckboxChange) {
    this.report.shukkin = event.checked ? this.getRemaining() : null;
  }

  onFullTransferChange(event: MatCheckboxChange) {
    this.report.transfer_amount = event.checked ? this.getRemaining() : null;
  }

}
